import { Card, Col, Form, Row, Space } from 'antd';
import { useSelector } from 'react-redux';
import { useDebounce, useToggle } from 'react-use';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';
import { DownOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { useTableOptions } from '../../../shared/hooks/use-data-options';
import { Feature } from '../../../shared/models/features';
import { SourcesTable, SourcesTableFilters, SourcesTableFiltersForm } from '../../components/sources-table';
import { translationNamespace } from '../../constants/translation-resources';
import { useFindSourcesQuery } from '../../data/queries/source-queries';
import { Source, SourcesFilters } from '../../models/source';
import { convertTableFiltersToFilters } from '../../data/converters/source-converters';
import { Orders } from '../../../shared/constants/order';
import { CustomSearch } from '../../../shared/components/search';
import { CustomFilterButton } from '../../../shared/components/filter-button';
import { FixedButton } from '../../../shared/components/fixed-button';
import { permissionSelector } from '../../../shared/data/store/selectors/auth-selectors';

export const SourcesListView: FC = () => {
  const { t } = useTranslation(translationNamespace);
  const [filtersForm] = Form.useForm<SourcesTableFilters>();
  const { page, setPage, pageSize, order, handleTableChange } = useTableOptions<Source>([['createdAt', Orders.DESC]]);
  const [showFilters, toggleShowFilters] = useToggle(false);
  const [filters, setFilters] = useState<SourcesFilters>(null);
  const [searchValue, setSearchValue] = useState<string>();
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>();
  const { data, isLoading } = useFindSourcesQuery({
    page,
    pageSize,
    searchValue: debouncedSearchValue,
    filters,
    order,
  });
  const { url } = useRouteMatch();
  const permissions = useSelector(permissionSelector);

  const applyFilters = (filters: SourcesTableFilters) => {
    const sourcesFilters = convertTableFiltersToFilters(filters);

    setSearchValue(null);
    setDebouncedSearchValue(null);
    setFilters(sourcesFilters);
    setPage(1);
  };

  const applySearch = (value: string) => {
    setFilters(null);
    filtersForm.resetFields();
    setSearchValue(value);
    setDebouncedSearchValue(value);
    setPage(1);
  };

  useDebounce(
    () => {
      if (searchValue != null) {
        setFilters(null);
        filtersForm.resetFields();
        setPage(1);
      }
      setDebouncedSearchValue(searchValue);
    },
    3000,
    [searchValue]
  );

  return (
    <Row>
      <StyledSpace>
        <CustomSearch
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          placeholder={t(`${Feature.SHARED}:search`)}
          onSearch={applySearch}
          allowClear
          size="large"
        />
        <CustomFilterButton type="link" onClick={toggleShowFilters}>
          {t(`${Feature.SHARED}:filters`)} <StyledDownOutlined rotate={showFilters ? 180 : 0} />
        </CustomFilterButton>
      </StyledSpace>
      {permissions.SOURCE.CREATE && (
        <Link to={`${url}/new`}>
          <FixedButton type="primary" size="large">
            {t('sources.list.new')}
          </FixedButton>
        </Link>
      )}
      {showFilters && (
        <Col span={24} css={marginTop}>
          <Card title={t(`${Feature.SHARED}:filters`)}>
            <SourcesTableFiltersForm form={filtersForm} onApply={applyFilters} />
          </Card>
        </Col>
      )}
      <Col span={24} style={{ marginTop: showFilters || isLoading || data?.total === 0 ? '50px' : '0' }}>
        <SourcesTable
          dataSource={data?.resources}
          pagination={{
            total: data?.total,
            current: page,
            pageSize,
            showSizeChanger: data?.total > 10,
            position: ['topRight'],
          }}
          loading={isLoading}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
        />
      </Col>
    </Row>
  );
};

const StyledDownOutlined = styled(DownOutlined)`
  > svg {
    transition: transform 0.2s;
  }
`;

const StyledSpace = styled(Space)`
  position: absolute;
  z-index: 1;

  .ant-space-item {
    height: 38px;
  }
`;

const marginTop = css`
  margin-top: 50px;
`;
